import { mentionLimitMessage } from "../../utils/mentions";

// User-facing messages for failed comment requests. Raw backend payloads are
// never shown; only the HTTP status is used (and, for the @mention limit, the
// field the 400 names: see utils/mentions.mentionLimitMessage).

const statusOf = (err) => err?.response?.status;

export const isBadRequest = (err) => statusOf(err) === 400;

// Likes and reactions. `action` reads like "update your like".
export function interactionErrorMessage(err, action) {
  switch (statusOf(err)) {
    case 400:
      return `Couldn't ${action}. The comment may have been deleted or the post has expired.`;
    case 401:
      return "Your session has expired. Please sign in again.";
    case 403:
      return "You don't have permission to do that.";
    case 404:
      return "This comment is no longer available.";
    default:
      return `Couldn't ${action}. Check your connection and try again.`;
  }
}

// Create, edit, and delete.
export function mutationErrorMessage(err, kind) {
  const mentionLimit = mentionLimitMessage(err);
  if (mentionLimit) return mentionLimit;
  switch (statusOf(err)) {
    case 400:
      if (kind === "edit") return "Couldn't save your edit. The comment may have been deleted.";
      if (kind === "delete") return "Couldn't delete this comment.";
      return "Couldn't post. The comment may have been deleted or the post has expired.";
    case 401:
      return "Your session has expired. Please sign in again.";
    case 403:
      return "You don't have permission to do that.";
    case 404:
      return "This comment is no longer available.";
    default: {
      const verb = kind === "edit" ? "save your edit" : kind === "delete" ? "delete" : "post";
      return `Couldn't ${verb}. Check your connection and try again.`;
    }
  }
}

// --- Event occurrences -----------------------------------------------------
// The occurrence endpoints answer 409 { code } when engagement isn't open:
// the code (never the payload's text) picks the message.

export const ENGAGEMENT_UNAVAILABLE = {
  CANCELED: "occurrence_canceled",
  NOT_STARTED: "occurrence_not_started",
  PRIVATE: "occurrence_private",
};

export const engagementUnavailableCode = (err) =>
  statusOf(err) === 409 ? err?.response?.data?.code ?? null : null;

export function engagementUnavailableMessage(code) {
  switch (code) {
    case ENGAGEMENT_UNAVAILABLE.CANCELED:
      return "This event was canceled. Existing likes and comments stay, but nothing new can be added.";
    case ENGAGEMENT_UNAVAILABLE.NOT_STARTED:
      return "Likes and comments open when this event starts.";
    case ENGAGEMENT_UNAVAILABLE.PRIVATE:
      return "Private events don't have likes or comments.";
    default:
      return "Likes and comments aren't available for this event.";
  }
}

// Whether a failed occurrence request means the viewer's access or the
// occurrence's state may have changed (re-check the preview).
export const isAccessError = (err) => [403, 404, 409].includes(statusOf(err));

function occurrenceMessage(err, fallback) {
  if (statusOf(err) === 409) return engagementUnavailableMessage(engagementUnavailableCode(err));
  if (statusOf(err) === 404) return "This event or comment is no longer available.";
  return fallback;
}

// Comment errors on an occurrence: as for posts, with the event's own reasons.
export const occurrenceCommentErrorMessages = {
  interaction: (err, action) =>
    occurrenceMessage(
      err,
      statusOf(err) === 400
        ? `Couldn't ${action}. The comment may have been deleted.`
        : interactionErrorMessage(err, action)
    ),
  mutation: (err, kind) =>
    mentionLimitMessage(err) ??
    occurrenceMessage(
      err,
      statusOf(err) === 400 && kind === "create"
        ? "Couldn't post. The comment you're replying to may have been deleted."
        : mutationErrorMessage(err, kind)
    ),
};
